/**
 * -6차(마이너스6차) 관리 (/adm/msix)
 * ($j.Pages.Admin.Msix / body data-page-name="Msix")
 *
 * 탭 5개.
 *   현황 — KPI + 30일 흐름 + 보유 일차 분포 + 소멸 사유 + 상위 보유자 (첫 화면)
 *   순위 — 보유자를 점수순으로. 같은 점수를 등급방식(점수순위/퍼센타일/균등분할)별로 나란히 비교
 *   내역 — 검색·상태필터 목록. 행을 누르면 그 -6차 한 건의 라이프사이클 타임라인.
 *          **원장에서 grant 를 찾아** 회수하는 자리이기도 하다 (오더번호로 찾는 경로)
 *   지급 — 회원을 골라 1건 지급 + 수동 지급 내역 검색 (Msix/Give.js)
 *   회수 — 회원을 골라 즉시회수·회수예약 + 회수 예약/내역 검색 (Msix/Recall.js)
 *
 * **지급과 회수는 탭이 따로다** — 방향이 반대인 일을 한 판에 두면 서로의 칸을 지나야 한다.
 * 회수 탭은 제재관리($j.Pages.Admin.Penalty 「-6차 회수」탭)와 **같은 모듈·같은 블레이드 조각**이다.
 *
 * 주소: `?tab=give|recall&mem=이름#번호` — 그 탭을 열고 회원 판까지 띄운다.
 *       `?tab=give&onum=오더번호` — 지급탭의 '오더번호로 지급' 을 그 오더로 조회해 둔다.
 *
 * 차트는 라이브러리 없이 CSS 막대로 그린다(관리자 번들에 차트 의존성을 늘리지 않으려고).
 */
import Give from './Msix/Give';
import Recall from './Msix/Recall';
import Util from '../../Common/Util';

export default class Msix {
    // 뱃지 색 슬롯의 순서표 — 여기 순서가 곧 SCSS 의 .s0~.s16 / .k0~.k5 색이다.
    static SIDOS = ['서울', '경기', '인천', '강원', '충북', '충남', '대전', '세종', '전북',
                    '전남', '광주', '경북', '대구', '경남', '부산', '울산', '제주'];
    static CARS  = ['스카이', '사다리', '크레인', '지게차', '굴삭기', '리프트'];

    initialize() {
        this.tab = 'stats';
        this.status = '';
        this.table = null;
        this.stats = null;

        // 지급 · 회수 탭 — 회수는 제재관리와 공유하는 모듈
        this.give   = new Give({ onChange: () => this._loadStats() });
        this.recall = new Recall({ onChange: () => this._loadStats() });
        this.give.start();
        this.recall.start();

        this._bindTabs();
        this._bindList();

        // 순위 탭 — 필터를 바꾸면 다시 조회. 정렬·페이징은 DataTables 가 한다.
        $('#msRankReload').on('click', () => this._loadRank());
        $('#msRankCar, #msRankSido').on('change', () => this._loadRank());

        this._bindTrendHover();
        this._loadStats();
        this._openFromQuery();
    }

    destroy() {
        if (this.table)     { this.table.destroy();     this.table = null; }
        if (this.rankTable) { this.rankTable.destroy(); this.rankTable = null; }
        if (this.give)      { this.give.destroy();      this.give = null; }
        if (this.recall)    { this.recall.destroy();    this.recall = null; }
    }

    // ─────────────────────────────────────────────── 탭

    _bindTabs() {
        $('#msTabs').on('click', 'a', (e) => {
            e.preventDefault();
            this._openTab($(e.currentTarget).data('tab'));
        });
    }

    _openTab(tab) {
        if (!tab || tab === this.tab || !$(`#msTabs a[data-tab="${tab}"]`).length) return;
        this.tab = tab;

        $('#msTabs a').removeClass('active').filter(`[data-tab="${tab}"]`).addClass('active');
        // 탭 안 조각에도 pane 이 있을 수 있어 페이지 바로 아래로 좁힌다
        $('#msRoot > .tab-content > .ms-pane').prop('hidden', true).filter(`[data-pane="${tab}"]`).prop('hidden', false);

        if (tab === 'rank' && !this._rankLoaded) this._loadRank();
        if (tab === 'list' && !this.table)       this._initTable();
        if (tab === 'give')   this.give.open();
        if (tab === 'recall') this.recall.open();
    }

    /** `?tab=give|recall&mem=이름#번호` · `?tab=give&onum=번호` — 다른 화면이 회원·오더를 지목해 데려올 때 */
    _openFromQuery() {
        const q   = new URLSearchParams(location.search);
        const tab = q.get('tab');
        const mem = q.get('mem');
        if (!tab) return;

        this._openTab(tab);
        const pane = tab === 'give' ? this.give : (tab === 'recall' ? this.recall : null);
        if (pane && mem) {
            if (tab === 'give') this.give.setMode('member');
            pane.el('mem').val(mem).trigger('change');
        }
        if (tab === 'give' && q.get('onum')) this.give.lookupOrder(q.get('onum'));
    }

    // ─────────────────────────────────────────────── 현황

    async _loadStats() {
        try {
            const r = await $.ajax({ url: '/adm/ajax/msix/stats', type: 'get', dataType: 'json' });
            if (!r.ok) return;
            this.stats = r;
            this._renderKpi(r);
            this._renderTrend(r.trend);
            this._renderByDay(r.byDay, r.expire);
            this._renderEnded(r.ended);
            this._renderTop(r.top);
        } catch (e) {
            console.error('-6차 현황 로드 실패', e);
        }
    }

    _renderKpi(r) {
        const k = r.kpi;
        const card = (label, value, sub, cls) => `
            <div class="ms-kpi-card ${cls || ''}">
                <div class="ms-kpi-label">${label}</div>
                <div class="ms-kpi-value">${value}</div>
                <div class="ms-kpi-sub">${sub || '&nbsp;'}</div>
            </div>`;

        const t = k.today;
        $('#msKpi').html(
            card(`${$j.Common.Util.resItem('msix')} 보유중`, this._n(k.active) + '<small>건</small>', `보유 회원 ${this._n(k.holders)}명`) +
            card('보유 점수 합계', this._n(k.activePt) + '<small>원</small>', '매칭 순위의 기준') +
            card('오늘 발급', this._n(t['발급']) + '<small>건</small>', '작업완료 → 발주자 적립') +
            card('오늘 소진', this._n(t['사용'] + t['전환'] + t['회수']) + '<small>건</small>',
                 `수주 ${t['사용']} · 전환 ${t['전환']} · 회수 ${t['회수']}`) +
            card('회수 예약 대기', this._n(k.waitCnt) + '<small>개</small>',
                 k.waitRows > 0 ? `진행중 ${k.waitRows}건 · 「회수」탭에서 처리` : '진행중인 예약 없음',
                 k.waitRows > 0 ? 'is-warn' : '')
        );
    }

    /** 최근 30일 — 발급(위) vs 소진(아래) 대칭 막대 */
    _renderTrend(trend) {
        if (!trend.length) { $('#msTrend').html(this._empty()); return; }

        const rows = trend.map(d => ({
            date: d.date,
            give: d['발급'] | 0,
            gone: (d['사용'] | 0) + (d['전환'] | 0) + (d['회수'] | 0),
            use:  d['사용'] | 0, conv: d['전환'] | 0, back: d['회수'] | 0,
        }));
        const max = Math.max(1, ...rows.map(r => Math.max(r.give, r.gone)));

        this._trend = rows; // hover 툴팁이 참조
        const bars = rows.map((r, i) =>
            `<div class="ms-tr-col" data-i="${i}">
                <div class="ms-tr-up"><i style="height:${Math.round(r.give / max * 100)}%"></i></div>
                <div class="ms-tr-axis"></div>
                <div class="ms-tr-dn"><i style="height:${Math.round(r.gone / max * 100)}%"></i></div>
            </div>`).join('');

        const first = rows[0].date.slice(5), last = rows[rows.length - 1].date.slice(5);
        $('#msTrend').html(`
            <div class="ms-legend">
                <span><i class="lg-give"></i>발급</span>
                <span><i class="lg-gone"></i>소진(수주·전환·회수)</span>
                <span class="text-muted ml-auto">막대(또는 그 자리)에 마우스를 올리면 그날 상세</span>
            </div>
            <div class="ms-trend">${bars}<div class="ms-trend-tip"></div></div>
            <div class="ms-axis-label"><span>${first}</span><span>${last}</span></div>`);
    }

    /**
     * 30일 흐름 막대 hover 툴팁.
     * 네이티브 title 은 지연이 있고 막대가 얇아 잘 안 잡혀서, 차트 안에 직접 띄운다.
     */
    _bindTrendHover() {
        const $wrap = $('#msTrend');
        $wrap.on('mouseenter', '.ms-tr-col', (e) => {
            const r = (this._trend || [])[$(e.currentTarget).data('i')];
            if (!r) return;

            const $tip = $wrap.find('.ms-trend-tip');
            $tip.html(`<b>${r.date}</b>
                <span class="tp-row"><i class="lg-give"></i>발급 <b>${this._n(r.give)}</b>건</span>
                <span class="tp-row"><i class="lg-gone"></i>소진 <b>${this._n(r.gone)}</b>건</span>
                <span class="tp-sub">수주 ${r.use} · 전환 ${r.conv} · 회수 ${r.back}</span>`);

            // 막대 중앙에 붙이되 차트 밖으로 넘치지 않게
            const wrapW = $wrap.find('.ms-trend').width();
            const col   = e.currentTarget;
            const cx    = col.offsetLeft + col.offsetWidth / 2;
            const tipW  = $tip.outerWidth();
            $tip.css('left', Math.max(0, Math.min(cx - tipW / 2, wrapW - tipW))).addClass('is-on');
        }).on('mouseleave', '.ms-trend', () => {
            $wrap.find('.ms-trend-tip').removeClass('is-on');
        });
    }

    /** 보유중 -6차가 며칠차인지 — 만료 임박(마지막 날)은 붉게 */
    _renderByDay(byDay, expire) {
        if (!byDay.length) { $('#msByDay').html(this._empty()); return; }
        const max = Math.max(1, ...byDay.map(d => parseInt(d.c)));

        const rows = byDay.map(d => {
            const n = parseInt(d.nday), c = parseInt(d.c);
            const near = n >= expire; // 오늘 자정 배치에서 발주개수로 전환될 것들
            return `<div class="ms-hbar ${near ? 'is-near' : ''}" title="${n}일차 · ${this._n(c)}건 · ${this._n(d.mem)}명 · ${this._n(d.pt)}원">
                <span class="ms-hbar-lb">${n}일차</span>
                <span class="ms-hbar-track"><i style="width:${Math.round(c / max * 100)}%"></i></span>
                <span class="ms-hbar-val">${this._n(c)}건 <em>${this._n(d.mem)}명</em> <em>${this._n(d.pt)}</em></span>
            </div>`;
        }).join('');

        $('#msByDay').html(rows + `
            <div class="text-muted mt-2" style="font-size:12px">
                ${expire}일차를 넘기면 자정 배치가 발주개수 1개로 전환합니다.
            </div>`);
    }

    _renderEnded(ended) {
        if (!ended.length) { $('#msEnded').html(this._empty()); return; }
        const total = ended.reduce((s, e) => s + parseInt(e.c), 0);
        const color = { '수주사용': '#22c55e', '발주개수전환': '#94a3b8', '오더취소': '#f59e0b', '관리자회수': '#ef4444' };

        $('#msEnded').html(ended.map(e => {
            const c = parseInt(e.c), pct = Math.round(c / total * 1000) / 10;
            return `<div class="ms-hbar">
                <span class="ms-hbar-lb">${this._esc(e.label)}</span>
                <span class="ms-hbar-track"><i style="width:${pct}%;background:${color[e.label] || '#94a3b8'}"></i></span>
                <span class="ms-hbar-val">${this._n(c)} <em>${pct}%</em></span>
            </div>`;
        }).join(''));
    }

    _renderTop(top) {
        if (!top.length) { $('#msTop').html(this._empty()); return; }
        const tr = top.map((t, i) => `<tr>
            <td class="text-center">${i + 1}</td>
            <td>${$j.F.Admin.Member.Pop.cell(t.mb_no, t.mem_name)}</td>
            <td class="text-center">${this._sidoBadge(t.sido)}</td>
            <td class="text-center">${this._carBadges(t.car_types)}</td>
            <td class="text-right">${this._n(t.cnt)}건</td>
            <td class="text-right"><b>${this._n(t.pt)}</b></td>
        </tr>`).join('');

        $('#msTop').html(`<table class="table table-bordered mb-0">
            <thead><tr>
                <th style="width:64px" class="text-center">순위</th><th>회원</th>
                <th style="width:64px"  class="text-center">시도</th>
                <th style="width:120px" class="text-center">업종</th>
                <th style="width:80px"  class="text-right">보유</th>
                <th style="width:120px" class="text-right">점수</th>
            </tr></thead><tbody>${tr}</tbody></table>`);
    }

    // ─────────────────────────────────────────────── 순위

    async _loadRank() {
        const q = {
            car_type: $('#msRankCar').val() || '',
            gcode:    $('#msRankSido').val() || '',
        };
        try {
            const r = await $.ajax({ url: '/adm/ajax/msix/ranking', type: 'get', data: q, dataType: 'json' });
            if (!r.ok) return;
            this._rankLoaded = true;
            this._fillRankFilters(r.filters);
            this._renderRank(r);
        } catch (e) {
            console.error('-6차 순위 로드 실패', e);
        }
    }

    /** 업종·시도 셀렉트는 첫 응답에서 한 번만 채운다(선택값은 유지) */
    _fillRankFilters(f) {
        if (this._filtersFilled || !f) return;
        this._filtersFilled = true;

        $('#msRankCar').html('<option value="">전체</option>'
            + (f.carTypes || []).map(v => `<option value="${this._esc(v)}">${this._esc(v)}</option>`).join(''));
        // 지역은 시도 단건이 아니라 매칭이 쓰는 그룹 단위(서울·경기·인천 …)
        $('#msRankSido').html('<option value="">전체</option>'
            + (f.regions || []).map(g => `<option value="${g.gcode}">${this._esc(g.label)}</option>`).join(''));
    }

    /**
     * 보유자 표 — 정렬·페이징은 DataTables 에 맡긴다(보유/점수/ver3 헤더 클릭).
     * ver3 는 화면엔 "N일차 M단계" 로 보이지만 정렬은 원시값 msix_odr 로 해야 순서가 맞다.
     */
    _renderRank(r) {
        const self = this;
        if (this.rankTable) { this.rankTable.destroy(); $('#msRankTable').empty(); this.rankTable = null; }

        this.rankTable = new DataTable('#msRankTable', {
            data: r.rows,
            autoWidth: false,
            pageLength: 20,
            lengthChange: false,
            searching: false,
            order: [[5, 'desc']],   // 기본 = 점수 많은순
            columns: [
                { data: null, title: '순위', width: '52px', orderable: false, className: 'text-center' },
                { data: 'mem_name', title: '회원',
                    render: (d, t, row) => $j.F.Admin.Member.Pop.cell(row.mb_no, d) },
                // 시도·업종·보유는 분류/참고용이라 정렬 대상에서 뺀다(정렬은 점수·ver3 만)
                { data: 'sido', title: '시도', width: '70px', className: 'text-center',
                    orderable: false, render: (d) => self._sidoBadge(d) },
                { data: 'car_types', title: '업종', width: '130px', className: 'text-center',
                    orderable: false, render: (d) => self._carBadges(d) },
                { data: 'cnt', title: '보유', width: '80px', className: 'text-right',
                    orderable: false, render: (d) => `${self._n(d)}건` },
                { data: 'pt', title: '점수', width: '130px', className: 'text-right',
                    render: (d, t) => t === 'display' ? `<b>${self._n(d)}</b>` : (parseInt(d) || 0) },
                { data: 'v3', title: 'ver3 기준', width: '120px', className: 'text-center',
                    // 정렬키는 msix_odr (작을수록 우선). 값이 없는 회원은 맨 뒤로.
                    render: (d, t, row) => {
                        if (t !== 'display') return row.msix_odr == null ? 9999 : parseInt(row.msix_odr);
                        return d ? `<span class="ms-v3">${self._esc(d)}</span>` : '<span class="text-muted">-</span>';
                    } },
            ],
            // 순위 열은 "지금 정렬 기준의 몇 번째" — 정렬이 바뀌면 다시 매긴다.
            drawCallback: function () {
                const api = this.api();
                const start = api.page.info().start;
                api.column(0, { order: 'applied', page: 'current' }).nodes()
                   .each((cell, i) => { cell.innerHTML = start + i + 1; });
            },
            language: {
                emptyTable: '해당 조건에 -6차 보유자가 없습니다.',
                info: '_START_–_END_ / 총 _TOTAL_명', infoEmpty: '0명', infoFiltered: '',
                paginate: { previous: "<i class='fa fa-chevron-left'></i>", next: "<i class='fa fa-chevron-right'></i>" },
            },
        });
    }

    // ─────────────────────────────────────────────── 내역

    _bindList() {
        $('#msSearch').on('click', () => this._reload());
        $('#msKwd, #msOrder').on('keydown', (e) => { if (e.key === 'Enter') this._reload(); });
        $('#msReset').on('click', () => {
            $('#msKwd, #msOrder').val('');
            this.status = '';
            $('.ms-status .btn').removeClass('active').filter('[data-status=""]').addClass('active');
            this._reload();
        });
        $('.ms-status').on('click', '.btn', (e) => {
            $('.ms-status .btn').removeClass('active');
            $(e.currentTarget).addClass('active');
            this.status = $(e.currentTarget).data('status');
            this._reload();
        });
        $('#msTakeBack').on('click', () => this._takeBack());
    }

    _initTable() {
        const self = this;
        this.table = new DataTable('#msTable', {
            autoWidth: false,
            processing: true, serverSide: true, ordering: false,
            pageLength: 20, lengthChange: false, searching: false,
            ajax: {
                url: '/adm/ajax/msix/list', type: 'post',
                data: (d) => {
                    d.findUserKwd  = $('#msOrder').val() || $('#msKwd').val() || '';
                    d.statusFilter = self.status;
                },
            },
            columns: [
                { data: null, orderable: false, width: '30px', className: 'text-center',
                    render: (d) => d.used_date ? '' : `<input type="checkbox" class="ms-chk" value="${d.num}">` },
                { data: 'mem_name', title: '회원',
                    render: (d, t, row) => $j.F.Admin.Member.Pop.cell(row.mb_no, d) },
                { data: 'ordernum', title: '발주오더', className: 'text-center',
                    render: (d) => d ? `<a href="javascript:;" class="ms-order" data-o="${d}">${d}</a>`
                                     : '<span class="text-muted">관리자지급</span>' },
                { data: 'start_date', title: '발급일', className: 'text-center' },
                { data: null, title: '점수', className: 'text-right',
                    render: (d) => d.initial_pt == null ? '<span class="text-muted">—</span>'
                        : `${self._n(d.current_pt)}<span class="text-muted"> / ${self._n(d.initial_pt)}</span>` },
                { data: null, title: '상태', className: 'text-center', render: (d) => self._statusBadge(d) },
                { data: 'manage_name', title: '등록자', className: 'text-center',
                    render: (d) => (d ? Util.escapeHtml(d) : '<span class="text-muted">—</span>') },
                { data: 'memo', title: '사유', render: (d) => self._esc(d || '') },
            ],
            language: {
                emptyTable: '데이터가 없습니다.', loadingRecords: '로딩 중...',
                zeroRecords: '일치하는 결과가 없습니다.', info: '_START_–_END_ / _TOTAL_건',
                infoEmpty: '0건', infoFiltered: '',
                paginate: { previous: "<i class='fa fa-chevron-left'></i>", next: "<i class='fa fa-chevron-right'></i>" },
            },
        });

        // 행 클릭 → 라이프사이클 (회원/오더 링크·체크박스는 제외)
        $('#msTable tbody').on('click', 'tr', (e) => {
            if ($(e.target).is('input, a')) return;
            const d = this.table.row(e.currentTarget).data();
            if (d) this._openLife(d);
        });
        $('#msTable tbody').on('change', '.ms-chk', () => {
            const n = $('#msTable .ms-chk:checked').length;
            $('#msTakeBack').prop('disabled', n === 0).text(n ? `선택 회수 ${n}건` : '선택 회수');
        });
        $('#msTable tbody').on('click', '.ms-order', (e) => {
            e.stopPropagation();
            $j.F.Admin.Util.openOrderHandle($(e.currentTarget).data('o'));
        });
    }

    _reload() {
        if (!this.table) { this._initTable(); return; }
        $('#msTakeBack').prop('disabled', true).text('선택 회수');
        this.table.ajax.reload();
    }

    _statusBadge(d) {
        const u = parseInt(d.used_ordernum);
        if (d.used_date) {
            if (u > 0)  return `<span class="ms-badge is-used">수주사용</span>`;
            if (u === -1) return `<span class="ms-badge is-cancel">오더취소</span>`;
            if (u === -2) return `<span class="ms-badge is-conv"><i class="fas fa-heart res-i is-odr"></i> 발주개수전환</span>`;
            if (u === -3) return `<span class="ms-badge is-back">관리자회수</span>`;
            return `<span class="ms-badge">사용</span>`;
        }
        return `<span class="ms-badge is-live">보유중 ${d.nday}일차</span>`;
    }

    /** -6차 한 건의 라이프사이클 타임라인 */
    async _openLife(d) {
        try {
            const r = await $.ajax({ url: `/adm/ajax/msix/${d.num}/logs`, type: 'get', dataType: 'json' });
            const m = r.msix || {};
            const icon = { '발급': 'fa-plus', '사용': 'fa-check', '전환': 'fa-exchange-alt',
                           '회수': 'fa-undo', '취소-오더': 'fa-times', '취소-보정': 'fa-wrench' };

            const items = (r.logs || []).map(l => `
                <div class="ms-life-item">
                    <span class="ms-life-dot act-${this._esc(l.action)}"><i class="fas ${icon[l.action] || 'fa-circle'}"></i></span>
                    <div class="ms-life-body">
                        <div><b>${this._esc(l.action)}</b>
                            ${l.ref_ordernum > 0 ? `<a href="javascript:;" class="ms-order" data-o="${l.ref_ordernum}">오더 ${l.ref_ordernum}</a>` : ''}
                            ${l.nday ? `<span class="text-muted">${l.nday}일차</span>` : ''}</div>
                        <div class="text-muted">${this._esc(l.datetime || '')}
                            ${l.memo ? ' · ' + this._esc(l.memo) : ''}</div>
                    </div>
                </div>`).join('');

            const body = `<div class="ms-life">
                    <div class="ms-life-head">
                        발주오더 ${m.ordernum || '<span class="text-muted">관리자지급</span>'}
                        · 발급일 ${m.start_date || '-'}
                        ${m.used_date ? ` · 종료 ${m.used_date}` : ' · <b>보유중</b>'}
                    </div>
                    ${items || '<div class="text-muted p-3 text-center">기록된 이력이 없습니다.</div>'}
                   </div>`;
            $j.Ui.JBModal.html('msix-life-modal', body,
                `${this._esc(m.mem_name || '')} — -6차 #${d.num} 이력`, { width: '620px' });
        } catch (e) {
            console.error('라이프사이클 로드 실패', e);
            Util.swalert('이력을 불러오지 못했습니다.');
        }
    }

    _takeBack() {
        const nums = $('#msTable .ms-chk:checked').map((i, el) => el.value).get();
        if (!nums.length) return;

        const memo = ($('#msBackMemo').val() || '').trim();
        if (!memo) { Util.swalert('회수 사유를 입력하세요.'); $('#msBackMemo').focus(); return; }

        Util.swalert(`${nums.length}건을 회수할까요? 되돌릴 수 없습니다.`, () => {
            $.ajax({
                url: '/adm/ajax/msix/cancel', type: 'post',
                data: { chk: nums, memo },
                success: (r) => {
                    if (!r.result) { Util.swalert('회수에 실패했습니다.'); return; }
                    Util.swalert('회수했습니다.', () => {
                        $('#msBackMemo').val('');
                        this._reload();
                        this._loadStats();
                    });
                },
            });
        }, ['예', '아니오']);
    }

    // ─────────────────────────────────────────────── 공용

    _empty() { return '<div class="text-muted text-center py-4">데이터가 없습니다.</div>'; }

    /** 시도 뱃지 — 값마다 고유 색(색 슬롯은 SCSS 의 .s0~.s16) */
    _sidoBadge(v) {
        if (!v) return '<span class="text-muted">-</span>';
        const i = Msix.SIDOS.indexOf(v);
        return `<span class="ms-sido s${i >= 0 ? i : 99}">${this._esc(v)}</span>`;
    }

    /** 업종 뱃지 — 한 소유자가 여러 업종을 가질 수 있다(콤마 구분). 현황·순위 표 공용 */
    _carBadges(csv) {
        if (!csv) return '<span class="text-muted">-</span>';
        return String(csv).split(',').map(v => {
            const i = Msix.CARS.indexOf(v);
            return `<span class="ms-car k${i >= 0 ? i : 99}">${this._esc(v)}</span>`;
        }).join(' ');
    }
    _n(v)    { return Number(v ?? 0).toLocaleString(); }
    _esc(s)  {
        return String(s ?? '').replace(/[&<>"']/g, c => ({
            '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
        }[c]));
    }
}
